import {
  calculationToCollabMapping,
  CardTypeToCollabMapping,
  CardWidthToCollabMapping,
  coverFieldIdToCollabMapping,
  filterToCollabJSON,
  filterUsageTypeToCollabMapping,
  groupSortTypeToCollabMapping,
  viewToCollabJSON
} from '@databases/collab/convert/to-collab';
import { getDirectParentFilter } from '@databases/filters/utils/getDirectParentFilter';
import { getNestedFilter } from '@databases/filters/utils/getNestedFilter';
import {
  CollabFilterType,
  type MutableCollabFilter,
  type MutableCollabFilterBooleanExpression
} from '@databases/model/views/view-filter';
import { DatabaseFilterType } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { v4 as uuidv4 } from 'uuid';

import { type YJSChangeHandler } from '../../types.ts';
import { applyDeltaValue } from '../../utils/apply-delta-value.ts';

export const bulkApplySavedViewChanges: YJSChangeHandler<
  DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES
> = ({ ydb, change }) => {
  ydb.mutate((db) => {
    change.changes.forEach((viewChange) => {
      switch (viewChange.type) {
        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.layoutType = CardTypeToCollabMapping[viewChange.newLayoutType];
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.cardWidth = CardWidthToCollabMapping[viewChange.newCardWidth];
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.coverField = coverFieldIdToCollabMapping(viewChange.newCoverFieldId);
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_FIELD_NAMES: {
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.showFieldNames = viewChange.newShowFieldNames;
            }
          });
          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.groupFieldId = viewChange.newGroupFieldId;
          }

          break;
        }

        case DatabaseChangeType.SET_GROUPING_FIELD: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            let grouping = view.grouping;
            if (!grouping) {
              grouping = view.initGrouping();
            }

            if (viewChange.isSubgroup) {
              grouping.subgroupByField(viewChange.fieldId, viewChange.manualGroupOrder);
            } else {
              grouping.groupByField(viewChange.fieldId, viewChange.manualGroupOrder);
            }
          }

          break;
        }

        case DatabaseChangeType.SET_GROUPING_SORT_TYPE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            if (viewChange.isSubgroup) {
              const subgroupBy = view.grouping?.subgroupBy;
              if (subgroupBy) {
                subgroupBy.sortType = groupSortTypeToCollabMapping[viewChange.sortType];
              }
            } else {
              const groupBy = view.grouping?.groupBy;
              if (groupBy) {
                groupBy.sortType = groupSortTypeToCollabMapping[viewChange.sortType];
              }
            }
          }

          break;
        }

        case DatabaseChangeType.SET_GROUPING_MANUAL_GROUP_ORDER: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            if (viewChange.isSubgroup) {
              const subgroupBy = view.grouping?.subgroupBy;
              if (subgroupBy) {
                subgroupBy.manualGroupOrder = viewChange.manualGroupOrder;
              }
            } else {
              const groupBy = view.grouping?.groupBy;
              if (groupBy) {
                groupBy.manualGroupOrder = viewChange.manualGroupOrder;
              }
            }
          }

          break;
        }

        case DatabaseChangeType.CLEAR_GROUPING: {
          const view = db.viewsById[viewChange.viewId];

          if (view && view.grouping) {
            if (viewChange.isSubgroup) {
              view.grouping.clearSubgroupBy();
            } else {
              view.grouping.clearGroupBy();
            }
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.dateFieldId = viewChange.newDateFieldId;
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_NAME: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.applyNameDelta(viewChange.delta);
          }

          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW: {
          db.setView(viewChange.view.id, viewToCollabJSON(viewChange.view, {}));

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.setFieldVisibility(
              viewChange.fieldKey,
              viewChange.fieldValue,
              db.fields
            );
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sort = viewChange.sort;
            if (viewChange.sort && view.sorts.length <= 1) {
              view.sorts.clearAll();
              view.sorts.appendSort(viewChange.sort);
            }
            if (!viewChange.sort && view.sorts.length > 0) {
              view.sorts.clearAll();
            }
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_SORT_VALUE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.updateSortForFieldId(
              viewChange.fieldId,
              viewChange.ascending,
              viewChange.customOrder
            );
          }

          break;
        }

        case DatabaseChangeType.INSERT_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.insertSort(viewChange.index, viewChange.sort);
          }

          break;
        }

        case DatabaseChangeType.APPEND_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.appendSort(viewChange.sort);
          }

          break;
        }

        case DatabaseChangeType.DELETE_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.deleteSortForFieldId(viewChange.fieldId);
          }

          break;
        }

        case DatabaseChangeType.REORDER_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.reorderSortForFieldId(viewChange.fieldId, viewChange.newIndex);
          }

          break;
        }

        case DatabaseChangeType.CLEAR_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.clearAll();
          }

          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW_FILTER_USAGE_TYPE: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;

          if (view) {
            if (parentFilter) {
              // If a view has filters, the top level filter for the view will always be a
              // boolean CollabFilter. Valid boolean CollabFilters include AND and OR, with AND
              // being the default unless the user has explicitly changed the boolean type
              parentFilter = parentFilter as MutableCollabFilterBooleanExpression;

              if (viewChange.usageFilterId && viewChange.path) {
                const nestedFilterData = getNestedFilter(
                  parentFilter.value,
                  viewChange.usageFilterId,
                  viewChange.path
                );

                // A filter will be defined if there is a valid filter with an id matching the acted
                // upon filter. This validates that a collaborator has not removed the filter whose
                // value is being set.
                if (nestedFilterData) {
                  const filter =
                    nestedFilterData[0] as MutableCollabFilterBooleanExpression;

                  if (
                    filter.type === CollabFilterType.AND ||
                    filter.type === CollabFilterType.OR
                  ) {
                    filter.type = filterUsageTypeToCollabMapping(viewChange.usageType);
                  }
                }
              } else {
                parentFilter.type = filterUsageTypeToCollabMapping(viewChange.usageType);
              }
            }
          }

          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW_FILTER_VALUE: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;
          const updatedFilterFromView = viewChange.newFilter;

          if (view) {
            if (!parentFilter || viewChange.path === undefined) {
              view.setFilter({
                id: uuidv4(),
                type: CollabFilterType.AND,
                value: []
              });
            } else {
              // If a view has filters, the top level filter for the view will always be a
              // boolean CollabFilter. Valid boolean CollabFilters include AND and OR, with AND
              // being the default unless the user has explicitly changed the boolean type
              parentFilter = parentFilter as MutableCollabFilterBooleanExpression;
              const nestedFilterData = getNestedFilter(
                parentFilter.value,
                updatedFilterFromView.id,
                viewChange.path
              );

              // A filter will be defined if there is a valid filter with an id matching the acted
              // upon filter. This validates that a collaborator has not removed the filter whose
              // value is being set.
              if (nestedFilterData) {
                const filter = nestedFilterData[0] as MutableCollabFilter;
                // Updates the value of comparison based filters
                if (
                  viewChange.change &&
                  (filter.type === CollabFilterType.EQUALS ||
                    filter.type === CollabFilterType.GREATER ||
                    filter.type === CollabFilterType.LESS)
                ) {
                  applyDeltaValue((type) => filter.setValue(type), viewChange.change);
                }

                // Updates the value of a set based filter
                if (
                  filter.type === CollabFilterType.CONTAINS &&
                  updatedFilterFromView.type === DatabaseFilterType.CONTAINS
                ) {
                  filter.value = updatedFilterFromView.value;
                }
              }
            }
          }

          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW_FILTER:
        case DatabaseChangeType.SET_SAVED_VIEW_FILTER_FIELD_ID:
        case DatabaseChangeType.SET_SAVED_VIEW_FILTER_TYPE: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;

          if (view) {
            if (!viewChange.newFilter) {
              view.clearFilter();
            } else if (viewChange.path && viewChange.path.length === 0) {
              const collabFilter = filterToCollabJSON(viewChange.newFilter, {});
              if (collabFilter) {
                view.setFilter(collabFilter);
              }
            } else if (view.filter && viewChange.path) {
              // If a view has filters, the top level filter for the view will always be a
              // boolean CollabFilter. Valid boolean CollabFilters include AND and OR, with AND
              // being the default unless the user has explicitly changed the boolean type
              parentFilter = parentFilter as MutableCollabFilterBooleanExpression;
              const nestedFilterData = getNestedFilter(
                parentFilter.value,
                viewChange.newFilter.id,
                viewChange.path
              );

              // A filter will be defined if there is a valid filter with an id matching the acted
              // upon filter. This validates that a collaborator has not removed the filter whose
              // value is being set.
              if (nestedFilterData) {
                const [filter, index] = nestedFilterData;
                const collabFilter = filterToCollabJSON(viewChange.newFilter, {});
                // Operations are done on the direct parent of the altered or new filter
                const directParent = getDirectParentFilter(
                  parentFilter,
                  filter,
                  index
                ) as MutableCollabFilterBooleanExpression | undefined;

                if (directParent) {
                  // A collab filter will be defined if the filter has a valid value that can be
                  // converted to JSON
                  if (collabFilter) {
                    directParent.setFilter(filter.id, index, collabFilter);
                  } else {
                    directParent.remove(filter.id, index);

                    // Removes all filters if there are no children filters remaining
                    if (
                      directParent.value.length === 0 &&
                      directParent.id === view.filter.id
                    ) {
                      view.clearFilter();
                    }
                  }
                }
              }
            }
          }
          break;
        }

        case DatabaseChangeType.APPEND_SAVED_VIEW_FILTER: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;

          if (view) {
            // If a view has filters, the top level filter for the view will always be a
            // boolean CollabFilter. Valid boolean CollabFilters include AND and OR, with AND
            // being the default unless the user has explicitly changed the boolean type
            parentFilter =
              (parentFilter as MutableCollabFilterBooleanExpression) ??
              (view.setFilter({
                id: uuidv4(),
                type: CollabFilterType.AND,
                value: []
              }) as MutableCollabFilterBooleanExpression);

            if (viewChange.filterGroupId && viewChange.filterGroupPath) {
              // When a new filter is added to a filter group, the parent boolean usage filter will be
              // in the filter array and must be retrieved to add the new filter to
              const nestedFilterData = getNestedFilter(
                parentFilter.value,
                viewChange.filterGroupId,
                viewChange.filterGroupPath
              );

              if (nestedFilterData) {
                // Operations are done on the direct parent of the altered or new filter
                const directParent =
                  nestedFilterData[0] as MutableCollabFilterBooleanExpression;

                const collabFilter = filterToCollabJSON(viewChange.newFilter, {});

                // A collab filter will be defined if the filter has a valid value that can be
                // converted to JSON
                if (collabFilter) {
                  directParent.add(collabFilter);
                }
              }
            } else {
              // When a new filter is added, it will not yet be in the filter array and getNestedFilter
              // does not need to be called as there will not be a filter with the expected id
              const collabFilter = filterToCollabJSON(viewChange.newFilter, {});

              // A collab filter will be defined if the filter has a valid value that can be
              // converted to JSON
              if (collabFilter) {
                // Operations are done on the direct parent of the altered or new filter
                parentFilter.add(collabFilter);
              }
            }
          }
          break;
        }

        case DatabaseChangeType.REMOVE_SAVED_VIEW_FILTER: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;

          if (view) {
            if (!parentFilter || !viewChange.path) {
              view.clearFilter();
            } else {
              // If a view has filters, the top level filter for the view will always be a
              // boolean CollabFilter. Valid boolean CollabFilters include AND and OR, with AND
              // being the default unless the user has explicitly changed the boolean type
              parentFilter = parentFilter as MutableCollabFilterBooleanExpression;
              const nestedFilterData = getNestedFilter(
                parentFilter.value,
                viewChange.filterId,
                viewChange.path
              );

              // A filter will be defined if there is a valid filter with an id matching the acted
              // upon filter. This validates that a collaborator has not removed the filter whose
              // value is being set.
              if (nestedFilterData) {
                const [filter, index] = nestedFilterData;
                // Operations are done on the direct parent of the altered or new filter
                const directParent = getDirectParentFilter(
                  parentFilter,
                  filter,
                  index
                ) as MutableCollabFilterBooleanExpression | undefined;

                if (directParent) {
                  directParent.remove(viewChange.filterId, index);

                  // Removes the filter group usage boolean filter if there are no children
                  // filters remaining
                  if (directParent.value.length === 0) {
                    const filterGroupData = getNestedFilter(
                      parentFilter.value,
                      directParent.id,
                      viewChange.path.slice(0, -1)
                    );

                    if (filterGroupData) {
                      const [filterGroup, filterGroupIndex] = filterGroupData;

                      parentFilter.remove(filterGroup.id, filterGroupIndex);
                    }
                  }
                }
              }

              // Removes all filters if there are no children filters or filter groups remaining
              if (parentFilter.value.length === 0) {
                view.clearFilter();
              }
            }
          }

          break;
        }

        case DatabaseChangeType.CLEAR_SAVED_VIEW_FILTER: {
          const view = db.viewsById[viewChange.viewId];
          view.clearFilter();
          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.setCalculation(
                viewChange.fieldId,
                calculationToCollabMapping[viewChange.value]
              );
            }
          });
          break;

        case DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.removeCalculation(viewChange.fieldId);
            }
          });
          break;
        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.showNumberedEntries = viewChange.newShowNumberedEntries;
            }
          });
      }
    });
  });
};
